/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { NavigationSettingAri } from '@atlassian/ari/navigation';
import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { JiraWorkspaceAri } from '@atlassian/ari/jira/workspace';
import { JiraNavigationSettingsAri } from '@atlassian/ari/jira/navigation-settings';
import { fg } from '@atlassian/jira-feature-gating';
import GSNQuery, {
	type GSNQueryPreloaderQuery,
} from './__generated__/GSNQueryPreloaderQuery.graphql.ts';

// Result matches the generics of the async component's EntryPointProps
// except for TComponentProps which is derived from `root`
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type EntryPointParams = EntryPointRouteParams & any;

type GSNSidebarSettingsBoardEntryPointComponent = EntryPointComponent<
	{ gsnSidebarContent: GSNQueryPreloaderQuery },
	Record<string, never>,
	{},
	Record<string, never>
>;

export const gsnSidebarSettingsBoardEntryPoint: EntryPoint<
	GSNSidebarSettingsBoardEntryPointComponent,
	EntryPointParams
> = createEntryPoint({
	root: JSResourceForUserVisible<GSNSidebarSettingsBoardEntryPointComponent>(
		/* webpackChunkName: "gsn-sidebar-board-settings-entry-point" */ './BoardSettingsSidebarEntrypoint.tsx',
		{ includeInShellOnlySsrBundle: true, entryFsSsrLiftImportToModule: true },
	),

	getPreloadProps(params: EntryPointParams) {
		/**
		 * IMPORTANT: The query variables should be the same as in other queries, in order to avoid multiple
		 *   network requests.
		 */
		const {
			tenantContext: { activationId, atlassianAccountId, cloudId },
			entryPoints = {},
		} = params;

		const entityAriUserSideNavSettings = JiraWorkspaceAri.create({
			siteId: cloudId,
			activationId,
		}).toString();
		const navPlatMenuDataEntityAri = NavigationSettingAri.create({
			activationId,
			firstPartyApp: 'jira',
			siteId: cloudId,
			settingId: 'nav-plat-l1-menu-data',
		}).toString();
		const navPlatSidebarDataEntityAri = NavigationSettingAri.create({
			activationId,
			firstPartyApp: 'jira',
			siteId: cloudId,
			settingId: 'nav-plat-sidebar-data',
		}).toString();

		function entityAri(scope: string) {
			return JiraNavigationSettingsAri.create({
				siteId: cloudId,
				activationId,
				navigationSettingScope: scope,
			}).toString();
		}

		const ownerAri =
			atlassianAccountId && IdentityUserAri.create({ userId: atlassianAccountId }).toString();

		return {
			// Queries your async component uses
			queries: {
				gsnSidebarContent: {
					// generated .graphql
					parameters: GSNQuery,
					// variables for your query
					variables: {
						cloudId,
						entityAriApps: entityAri('apps'),
						entityAriFilters: entityAri('filters'),
						entityAriPlans: entityAri('plans'),
						...(fg('jpd-enable-roadmaps-recents-fe')
							? { entityAriRoadmaps: entityAri('collections') }
							: {}),
						entityAriUserSideNavSettings,
						isAppsExpanded: false,
						isFiltersExpanded: false,
						isPlansExpanded: false,
						isProjectsExpanded: false,
						isOperationsExpanded: false,
						navPlatMenuDataEntityAri,
						navPlatSidebarDataEntityAri,
						ownerAri,
						isRoadmapUpsellEnabled: fg('jpd-roadmaps-in-jira'),
						fetchGlobalShortcutsAvailableProducts: Boolean(
							cloudId && ownerAri && fg('navx-4515-jira-gsn-gas-ssr'),
						),
						isItamEnabled: fg('itam_ftw'),
						itamStaticChecks: fg('itam_ftw')
							? [
									{
										resourceId: `ari:cloud:jira-servicedesk::site/${cloudId}`,
										permissionId: 'write',
									},
									{
										resourceId: `ari:cloud:jira::site/${cloudId}`,
										permissionId: 'manage',
									},
									{
										resourceId: `ari:cloud:cmdb::site/${cloudId}`,
										permissionId: 'manage',
									},
								]
							: [],
					},
					// fetch options
					options: {
						fetchPolicy: 'store-or-network' as const,
					},
				},
			},
			entryPoints,
		};
	},
});
